import { useId } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  completeRunningTask, earnedByCurrency, entryDuration, entryEarnings, formatMoney, isLive, isTaskOpen, nextSubTask, parentTask, projectCurrency, runningEntry, runningTask, startTimer, stopTimer, subTasks, taskLabel, trackedTime,
  type Dataset, type OperationFactory, type Task, type TimeEntry
} from "@otrack/domain";
import { Button, Icon, IconButton, Tooltip } from "@otrack/ui";
import { billingSummary } from "./billing-labels";
import { useGoogleView } from "./calendar/google";
import { collectItems, colorOf } from "./calendar/model";
import { projectLabel, projectRefs, targetLabel } from "./commands";
import { Monogram } from "./components";
import { useApp, useDataset, useFactory } from "./context";
import { href } from "./router";
import { dayRange, formatAgo, formatClock, formatDuration, formatStopwatch, HOUR, MINUTE } from "./time";

const FORGOTTEN_AFTER = 10 * HOUR;
const enter = { initial: { opacity: 0, y: 8 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.5, ease: [0.16, 1, 0.3, 1] as const } };

function sortedCurrencies(totals: Record<string, number>): Array<[string, number]> {
  return Object.entries(totals).sort(([a], [b]) => (a === "USD" ? -1 : b === "USD" ? 1 : a.localeCompare(b)));
}

/** Each digit rolls in when it changes; seconds sit a step quieter than hours and minutes. */
function Stopwatch({ ms }: { ms: number }) {
  const text = formatStopwatch(ms);
  const split = text.lastIndexOf(":");
  const digits = (part: string, offset: number) => [...part].map((char, index) => char === ":"
    ? <span key={`c${offset + index}`} className="ot-stopwatch__colon">:</span>
    : <span key={`${offset + index}-${char}`} className="ot-stopwatch__digit">{char}</span>);
  return <div className="ot-stopwatch" role="timer" aria-label={text}>
    <span aria-hidden="true">{digits(text.slice(0, split), 0)}</span>
    <span className="ot-stopwatch__seconds" aria-hidden="true">{digits(text.slice(split), split)}</span>
  </div>;
}

export interface QuickStart {
  projectId: string | null;
  taskId: string | null;
  description: string;
  kind: "next" | "resume" | "start";
  /** When this work last stopped; null when it hasn't been tracked. */
  last: number | null;
}

/** The entry's Task while it is still open to track; a done or archived Task isn't picked up again. */
const openTaskId = (dataset: Dataset, entry: TimeEntry): string | null => {
  const task = entry.taskId ? dataset.tasks[entry.taskId] : undefined;
  return task && isTaskOpen(dataset, task) ? task.id : null;
};

/**
 * The next open sub-task when the last entry's sub-task is done, the last entry to resume with its
 * Task, then what other recent entries tracked, then other recently used Projects to start fresh.
 */
export function quickStarts(dataset: Dataset): QuickStart[] {
  const entries = Object.values(dataset.timeEntries).filter((entry) => isLive(entry) && entry.end !== null).sort((a, b) => b.start - a.start);
  const last = entries[0];
  const lastTask = last?.taskId ? dataset.tasks[last.taskId] : undefined;
  const next = lastTask && isLive(lastTask) && lastTask.doneAt !== null ? nextSubTask(dataset, lastTask) : undefined;
  const items: QuickStart[] = [];
  if (next) items.push({ projectId: next.projectId, taskId: next.id, description: "", kind: "next", last: null });
  if (last) items.push({ projectId: last.projectId, taskId: openTaskId(dataset, last), description: last.description, kind: "resume", last: last.end });
  const projects = projectRefs(dataset);
  const startable = new Set(projects.map((ref) => ref.id));
  const offered = new Set(items.map((item) => `${item.projectId}:${item.taskId}`));
  const offer = (projectId: string, taskId: string | null, stopped: number | null) => {
    const key = `${projectId}:${taskId}`;
    if (items.length >= 3 || offered.has(key)) return;
    offered.add(key);
    items.push({ projectId, taskId, description: "", kind: "start", last: stopped });
  };
  for (const entry of entries.slice(1)) {
    if (items.length >= 3) break;
    if (entry.projectId && startable.has(entry.projectId)) offer(entry.projectId, openTaskId(dataset, entry), entry.end);
  }
  for (const ref of projects) {
    if (items.length >= 3) break;
    if (!items.some((item) => item.projectId === ref.id)) offer(ref.id, null, null);
  }
  return items;
}

const openSubTasks = (dataset: Dataset, task: Task) => subTasks(dataset, task.id).filter((child) => child.doneAt === null).length;

/** What Done will close, named before the click: "Stop and mark Refund flow done, with its 2 open sub-tasks". */
function doneHint(dataset: Dataset, task: Task): string {
  const open = openSubTasks(dataset, task);
  return `Stop and mark ${task.name} done${open ? `, with ${open === 1 ? "its open sub-task" : `its ${open} open sub-tasks`}` : ""}`;
}

/** Stops the Timer and completes its Task, like the Task's own checkbox would. */
function completeTask(dataset: Dataset, factory: OperationFactory, task: Task, elapsed: number) {
  const open = openSubTasks(dataset, task);
  const message = `Completed ${task.name}${open ? ` and ${open} ${open === 1 ? "sub-task" : "sub-tasks"}` : ""} at ${formatDuration(elapsed)}`;
  return [completeRunningTask(dataset, factory, Date.now()), message] as const;
}

/**
 * What the plan says right now or next, today: a calm line with a one-click Start, never a
 * notification. Google events come from this device's cache, which the app keeps fresh.
 */
function PlanLine() {
  const { now, act, google } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const feeds = useGoogleView(google, dataset.settings);
  const today = dayRange(now);
  const running = runningEntry(dataset);
  const plans = collectItems(dataset, feeds, today.from, today.to, now).plans
    .filter((plan) => !plan.allDay && plan.end > now && !(running && running.eventId === plan.linkId && running.eventOccurrence === plan.linkOccurrence));
  const current = plans.find((plan) => plan.start <= now && plan.logged.length === 0);
  const plan = current ?? plans.find((item) => item.start > now);
  if (!plan) return null;
  const start = () => {
    const operations = startTimer(dataset, { projectId: plan.projectId, taskId: plan.taskId, description: plan.title, eventId: plan.linkId, eventOccurrence: plan.linkOccurrence }, factory, Date.now());
    void act(operations, `Started ${plan.title || projectLabel(dataset, plan.projectId)}`);
  };
  const minutes = Math.round((plan.start - now) / 60_000);
  const when = current ? `until ${formatClock(plan.end)}` : minutes <= 60 ? `in ${minutes} min` : `at ${formatClock(plan.start)}`;
  return <div className="ot-plan-line" style={{ ["--pc" as string]: colorOf(dataset, plan.projectId) }}>
    <i className="ot-swatch" aria-hidden="true"/>
    <a className="ot-plan-line__text" href={href({ page: "calendar", view: "day", date: null })}>
      <span className="ot-plan-line__when">{current ? "Now" : "Next"}</span>
      <strong>{plan.title || projectLabel(dataset, plan.projectId)}</strong>
      <span className="ot-mono">{when}</span>
    </a>
    <Button tone="ghost" icon="play" onClick={start}>Start</Button>
  </div>;
}

/** The Client's Picture or initials; a folder for work without a Client, as on Projects. */
function ClientTile({ projectId, size = 36 }: { projectId: string | null; size?: number }) {
  const dataset = useDataset();
  const project = projectId ? dataset.projects[projectId] : undefined;
  const client = project?.clientId ? dataset.clients[project.clientId] : undefined;
  if (client) return <Monogram name={client.name} picture={client.picture} size={size}/>;
  return <span className="ot-monogram is-empty" style={{ width: size, height: size }} aria-hidden="true"><Icon name="folder"/></span>;
}

/** Who the work is for: the Client beside its Picture, then the Project in its color. Without a Client, the Project and how it bills. */
function Identity({ projectId, linked = false }: { projectId: string | null; linked?: boolean }) {
  const dataset = useDataset();
  const project = projectId ? dataset.projects[projectId] : undefined;
  const client = project?.clientId ? dataset.clients[project.clientId] : undefined;
  const named = project && <span className="ot-identity__project"><i className="ot-swatch" aria-hidden="true"/>{project.name}</span>;
  const body = <>
    <ClientTile projectId={projectId}/>
    <span className="ot-identity__text">
      {client ? <><strong>{client.name}</strong>{named}</> : <><strong>{named ?? projectLabel(dataset, projectId)}</strong>{project && <span>{billingSummary(dataset, project)}</span>}</>}
    </span>
  </>;
  return linked && project
    ? <a className="ot-identity" href={href({ page: "project", id: project.id })}>{body}</a>
    : <span className="ot-identity">{body}</span>;
}

/** The Task in its Project's ring, after its parent when it is a sub-task: "Refund flow › Partial refunds". */
function TaskName({ task }: { task: Task }) {
  const dataset = useDataset();
  const parent = parentTask(dataset, task);
  return <span className={`ot-timer__task ${task.doneAt !== null ? "is-done" : ""}`}>
    <i className="ot-timer__ring" aria-hidden="true"/>
    <span className="ot-timer__task-name">{parent && <span className="ot-timer__parent">{parent.name} › </span>}{task.name}</span>
  </span>;
}

/**
 * Time on the Task against its Estimate and how many of its sub-tasks are done, as its row on the
 * Project shows them. A sub-task reports its parent's, since the parent's Estimate covers it.
 */
function TaskProgress({ task }: { task: Task }) {
  const { now } = useApp();
  const dataset = useDataset();
  const scope = parentTask(dataset, task) ?? task;
  const children = subTasks(dataset, scope.id);
  const ids = new Set([scope.id, ...children.map((child) => child.id)]);
  const total = Object.values(dataset.timeEntries).reduce((sum, entry) => isLive(entry) && entry.taskId && ids.has(entry.taskId) ? sum + entryDuration(entry, now) : sum, 0);
  const estimate = scope.estimateMinutes === null ? null : scope.estimateMinutes * MINUTE;
  if (!total && estimate === null && !children.length) return null;
  const over = estimate !== null && total > estimate;
  const finished = children.filter((child) => child.doneAt !== null).length;
  return <span className="ot-timer__progress">
    {estimate !== null && <span className={`ot-task__meter ${over ? "is-over" : ""}`}><i style={{ transform: `scaleX(${Math.min(1, total / estimate)})` }}/></span>}
    <span className={`ot-mono ${over ? "is-over" : ""}`}>{formatDuration(total)}{estimate !== null ? ` of ${formatDuration(estimate)}` : " tracked"}</span>
    {children.length > 0 && <span className="ot-task__count"><Icon name="check-circle"/><span className="ot-mono">{finished}/{children.length}</span></span>}
  </span>;
}

const START_VERB = { next: "Start", resume: "Resume", start: "Start" } as const;

/** Why a start is offered: "“Refund emails”, stopped 25 min ago", "Last tracked yesterday". */
function startNote(item: QuickStart, now: number): string {
  if (item.kind === "next") return "Next open sub-task";
  if (item.last === null) return "Not tracked yet";
  const ago = formatAgo(item.last, now);
  if (item.kind === "start") return `Last tracked ${ago}`;
  return item.description ? `“${item.description}”, stopped ${ago}` : `Stopped ${ago}`;
}

/**
 * One way to start a Timer while none runs. The lead start is laid out like the running Timer, so
 * starting it only adds the clock; the rest are compact rows.
 */
function QuickStartButton({ item, lead = false }: { item: QuickStart; lead?: boolean }) {
  const { now, act } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const noteId = useId();
  const label = targetLabel(dataset, item.projectId, item.taskId);
  const verb = START_VERB[item.kind];
  const project = item.projectId ? dataset.projects[item.projectId] : undefined;
  const client = project?.clientId ? dataset.clients[project.clientId] : undefined;
  const found = item.taskId ? dataset.tasks[item.taskId] : undefined;
  const task = found && isLive(found) ? found : undefined;
  const note = startNote(item, now);
  const start = () => void act(startTimer(dataset, { projectId: item.projectId, taskId: item.taskId, description: item.description }, factory, Date.now()), `${item.kind === "resume" ? "Resumed" : "Started"} ${label}`);
  const common = { type: "button" as const, style: { ["--pc" as string]: colorOf(dataset, item.projectId) }, "aria-label": `${verb} ${label}`, "aria-describedby": noteId, onClick: start };

  if (lead) return <button {...common} className={`ot-quick__lead ${task ? "" : "is-bare"}`}>
    <span className="ot-timer__head">
      <Identity projectId={item.projectId}/>
      <span className="ot-button ot-quick__go" aria-hidden="true"><Icon name="play" weight="fill"/>{verb}</span>
    </span>
    {task && <TaskName task={task}/>}
    <span className="ot-timer__note" id={noteId}>{note}</span>
    {task && <TaskProgress task={task}/>}
  </button>;

  return <button {...common} className="ot-quick__item">
    <ClientTile projectId={item.projectId} size={32}/>
    <span className="ot-quick__text">
      <strong><i className="ot-swatch" aria-hidden="true"/>{project ? project.name : projectLabel(dataset, item.projectId)}{task ? ` › ${taskLabel(dataset, task)}` : ""}</strong>
      <span id={noteId}>{client ? `${client.name} · ${note}` : note}</span>
    </span>
    <span className="ot-quick__play" aria-hidden="true"><Icon name="play" weight="fill"/></span>
  </button>;
}

export function TimerCard() {
  const { now, act, openEntry } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const running = runningEntry(dataset);

  if (!running) {
    const starts = quickStarts(dataset);
    // Resuming or moving to the next sub-task leads; with only fresh starts, none stands out.
    const lead = starts[0] && starts[0].kind !== "start" ? starts[0] : undefined;
    const rest = lead ? starts.slice(1) : starts;
    return <section className="ot-timer is-idle" aria-label="Timer">
      <motion.div className="ot-timer__inner" key="idle" {...enter}>
        <div className="ot-timer__status">No timer running</div>
        <div className="ot-starts">
          {lead && <QuickStartButton item={lead} lead/>}
          {rest.length > 0 && <div className="ot-quick">
            {rest.map((item) => <QuickStartButton key={`${item.projectId}:${item.taskId}:${item.kind}`} item={item}/>)}
          </div>}
          {starts.length === 0 && <p className="ot-timer__hint">Type “start” and a project in the bar above.</p>}
        </div>
        <PlanLine/>
      </motion.div>
    </section>;
  }

  const elapsed = now - running.start;
  const forgotten = elapsed >= FORGOTTEN_AFTER;
  const value = entryEarnings(running, now);
  const project = running.projectId ? dataset.projects[running.projectId] : undefined;
  const found = running.taskId ? dataset.tasks[running.taskId] : undefined;
  const task = found && isLive(found) ? found : undefined;
  // A Fixed Project's Task price is Earned once the Task, or its parent for a sub-task, is done.
  const priced = task ? parentTask(dataset, task) ?? task : undefined;
  const price = project?.billingMode === "fixed" && priced && priced.fixedPrice !== null ? { amount: priced.fixedPrice, currency: projectCurrency(dataset, project) } : null;
  const open = runningTask(dataset);
  const valueText = value ? <><strong className="ot-mono">{formatMoney(value)}</strong> so far</>
    : price && priced ? <><strong className="ot-mono">{formatMoney(price)}</strong> when {priced === task ? "done" : `${priced.name} is done`}</>
    : project?.billingMode === "fixed" ? "Fixed price project" : running.billable ? "No hourly rate set" : "Non-billable";

  return <section className={`ot-timer is-running ${forgotten ? "is-forgotten" : ""}`} aria-label="Timer" style={{ ["--pc" as string]: colorOf(dataset, running.projectId) }}>
    <motion.div className="ot-timer__inner" key={`run:${running.id}`} {...enter}>
      <div className="ot-timer__head">
        <Identity projectId={running.projectId} linked/>
        <div className="ot-timer__status"><span className="ot-live" aria-hidden="true"/>{forgotten ? "Running over 10 hours" : `Running since ${formatClock(running.start)}`}</div>
      </div>
      <Stopwatch ms={elapsed}/>
      <button type="button" className={`ot-timer__project ${task ? "" : "is-bare"}`} onClick={() => openEntry(running.id)}>
        {task && <TaskName task={task}/>}
        <span className={`ot-timer__note ${running.description ? "" : "is-empty"}`}>{running.description || "Add a description"}</span>
        {task && <TaskProgress task={task}/>}
      </button>
      <div className="ot-timer__foot">
        <span className="ot-timer__value">{valueText}</span>
        <div className="ot-timer__actions">
          {open && <Tooltip label={doneHint(dataset, open)}>
            <Button icon="check" onClick={() => void act(...completeTask(dataset, factory, open, elapsed))}>Done</Button>
          </Tooltip>}
          <Button tone="accent" icon="stop" onClick={() => void act(stopTimer(dataset, factory, Date.now()), `Stopped at ${formatDuration(elapsed)}`)}>Stop</Button>
        </div>
      </div>
      <PlanLine/>
    </motion.div>
  </section>;
}

/**
 * The running Timer in the top bar, so it can be read, stopped, or its Task marked done from any
 * page. It is named by its Task when it has one. While nothing runs it offers to resume the last
 * entry, when there is one.
 */
export function LiveTimer() {
  const { now, act, openEntry } = useApp();
  const dataset = useDataset();
  const factory = useFactory();
  const running = runningEntry(dataset);
  const resume = running ? undefined : quickStarts(dataset).find((item) => item.kind === "resume");
  const resumeLabel = resume ? targetLabel(dataset, resume.projectId, resume.taskId) : "";
  const elapsed = running ? now - running.start : 0;
  const task = running?.taskId ? dataset.tasks[running.taskId] : undefined;
  const named = task && isLive(task) ? task : undefined;
  const open = runningTask(dataset);

  return <div className={`ot-live-timer ${elapsed >= FORGOTTEN_AFTER ? "is-forgotten" : ""}`}>
    <AnimatePresence initial={false}>
      {running && <motion.div
        key="run"
        className="ot-live-timer__clip"
        initial={{ width: 0, opacity: 0 }}
        animate={{ width: "auto", opacity: 1 }}
        exit={{ width: 0, opacity: 0 }}
        transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
      >
        <div className="ot-live-timer__run" role="group" aria-label="Running timer">
          <Tooltip label={targetLabel(dataset, running.projectId, named?.id ?? null)} placement="bottom" disabled={!named}>
            <button type="button" className="ot-live-timer__open" style={{ ["--pc" as string]: colorOf(dataset, running.projectId) }} onClick={() => openEntry(running.id)}>
              <i className="ot-swatch" aria-hidden="true"/>
              <span className="ot-live-timer__name">{named ? named.name : projectLabel(dataset, running.projectId)}</span>
              <span className="ot-live-timer__time ot-mono">{formatStopwatch(elapsed)}</span>
            </button>
          </Tooltip>
          {open && <Tooltip label={doneHint(dataset, open)} placement="bottom">
            <button type="button" className="ot-live-timer__done" aria-label={`Mark ${open.name} done`} onClick={() => void act(...completeTask(dataset, factory, open, elapsed))}>
              <Icon name="check" weight="bold"/>
            </button>
          </Tooltip>}
          <button type="button" className="ot-live-timer__stop" aria-label="Stop timer" onClick={() => void act(stopTimer(dataset, factory, Date.now()), `Stopped at ${formatDuration(elapsed)}`)}>
            <Icon name="stop" weight="fill"/>
          </button>
        </div>
      </motion.div>}
    </AnimatePresence>
    {resume && <IconButton
      className="is-ghost ot-live-timer__resume"
      icon="play"
      label={`Resume ${resumeLabel}`}
      tooltipPlacement="bottom"
      onClick={() => void act(startTimer(dataset, { projectId: resume.projectId, taskId: resume.taskId, description: resume.description }, factory, Date.now()), `Resumed ${resumeLabel}`)}
    />}
  </div>;
}

export function TodayCard() {
  const { now } = useApp();
  const dataset = useDataset();
  const today = dayRange(now);
  const time = trackedTime(dataset, today, now);
  const money = sortedCurrencies(earnedByCurrency(dataset, today, now));
  const billableShare = time.total ? Math.round(time.billable / time.total * 100) : 0;
  return <section className="ot-summary" aria-label="Today">
    <div className="ot-summary__label">Tracked today</div>
    <div className="ot-summary__figure">{formatDuration(time.total)}</div>
    <dl className="ot-summary__facts">
      <div><dt>Billable</dt><dd className="ot-mono">{formatDuration(time.billable)}<small>{time.total ? `${billableShare}%` : ""}</small></dd></div>
      <div><dt>Entries</dt><dd className="ot-mono">{time.entries}</dd></div>
      {money.length ? money.map(([currency, amount]) => <div key={currency}><dt>Earned, {currency}</dt><dd className="ot-mono">{formatMoney({ amount, currency })}</dd></div>)
        : <div><dt>Earned</dt><dd className="ot-summary__none">Nothing billable yet</dd></div>}
    </dl>
  </section>;
}
